"use client";

import React, { useState, useMemo, useRef, useEffect } from "react";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { IPageLink } from "@/sanity/globals/CTA";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "@/helpers/Image";
import { AnimatePresence, motion } from "framer-motion";

import chevronRight from "@/public/global/chevron-right.svg";
import { GROQImage } from "@/sanity/lib/types";
import FormattedDate from "@/components/global/countdown/FormattedDate";

// Helper function to extract plain text from PortableText blocks
function extractTextFromPortableText(
  content: any[],
  maxLength: number = 200,
): string {
  if (!content || !Array.isArray(content)) return "";

  const textParts: string[] = [];

  for (const block of content) {
    if (block._type === "block" && block.children) {
      for (const child of block.children) {
        if (child.text) {
          textParts.push(child.text);
          // Check if we've reached the desired length
          if (textParts.join(" ").length >= maxLength) {
            break;
          }
        }
      }
      // Break outer loop if we have enough text
      if (textParts.join(" ").length >= maxLength) {
        break;
      }
    }
  }

  const fullText = textParts.join(" ");
  if (fullText.length > maxLength) {
    return fullText.substring(0, maxLength).trim() + "...";
  }
  return fullText;
}

const getCategoryLabel = (value: string) => {
  const categoryMap: Record<string, string> = {
    // agencies: "Agenturen",
    // b2bsaas: "B2B SaaS",
    // coaching_consulting: "Coaching & Consulting",
    // general: "Allgemeine Dienstleister",
  };
  return categoryMap[value] || value;
};

export default function BlogGrid({
  items,
  uniqueTags = [],
  filter = true,
  className,
}: {
  items?: {
    _id: string;
    title: string;
    description?: string;
    slug: string;
    banner?: GROQImage;
    tags?: string[];
    content?: any[];
    author?: {
      name: string;
      image?: GROQImage;
    };
    pubDate?: string;
    isFeatured?: boolean;
  }[];

  uniqueTags?: string[];
  filter?: boolean;
  className?: string;
}) {
  if (!items || items.length === 0) return null;

  const [activeFilter, setActiveFilter] = useState<string>("alle");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [itemsToShow, setItemsToShow] = useState<number>(9);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset itemsToShow when filters change
  useEffect(() => {
    setItemsToShow(9);
  }, [activeFilter, debouncedSearchQuery]);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const filteredItems = useMemo(() => {
    let filtered = items;

    // Filter by tag
    if (activeFilter !== "alle") {
      filtered = filtered.filter((item) => item.tags?.includes(activeFilter));
    }

    // Filter by search query (using debounced value)
    if (debouncedSearchQuery.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
      );
    }

    return filtered;
  }, [items, activeFilter, debouncedSearchQuery]);

  const displayedItems = filteredItems.slice(0, itemsToShow);
  const hasMore = itemsToShow < filteredItems.length;

  const handleLoadMore = () => {
    setItemsToShow((prev) => Math.min(prev + 3, filteredItems.length));
  };

  return (
    <section className={cn("", className)}>
      <div className={`container flex flex-col items-center overflow-clip`}>
        {filter && uniqueTags.length > 0 && (
          <div className="w-[min(180rem,100%)] mx-auto my-[4rem] lg:my-[7.5rem]">
            <div className="flex flex-col-reverse items-stretch lg:flex-row gap-[2rem] lg:gap-[3rem]">
              {/* Tags carousel */}
              <div className="relative flex-1">
                {/* Left gradient overlay */}
                <div className="hidden absolute top-0 left-0 z-10 h-full bg-gradient-to-r from-white to-transparent pointer-events-none md:block w-[6rem]" />

                {/* Left scroll button */}
                <button
                  onClick={() => scroll("left")}
                  className="hidden absolute top-1/2 z-20 justify-center items-center text-white rounded-full transition-all -translate-y-1/2 pointer-events-auto md:flex left-[1rem] w-[4rem] h-[4rem] bg-[#103037] hover:bg-[#103037]/90"
                  aria-label="Scroll left"
                >
                  <svg
                    className="rotate-180 w-[1.2rem] h-[1.2rem]"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 8 11"
                    fill="none"
                  >
                    <path
                      d="M0.668945 0.743164L5.66895 5.24316L0.668945 9.74316"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>

                {/* Scrollable pills container */}
                <div
                  ref={scrollContainerRef}
                  className="flex overflow-x-auto overflow-y-hidden justify-start w-full gap-[1.25rem] scrollbar-hide md:px-[6rem] lg:gap-[1.25rem]"
                >
                  <div className="hidden flex-shrink-0 md:block w-[1rem]" />
                  <button
                    onClick={() => setActiveFilter("alle")}
                    className={`whitespace-nowrap flex-shrink-0 rounded-full px-[3.25rem] py-[1.5rem] text-[1.75rem] lg:text-[2rem] leading-none transition-all ${
                      activeFilter === "alle"
                        ? "bg-[#103037] text-white"
                        : "bg-white border border-[#10303733] text-[#10303799] hover:bg-black/5"
                    }`}
                  >
                    Alle
                  </button>

                  {uniqueTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActiveFilter(tag)}
                      className={`whitespace-nowrap flex-shrink-0 rounded-full px-[3.25rem] py-[1.5rem] text-[1.75rem] lg:text-[2rem] leading-none transition-all ${
                        activeFilter === tag
                          ? "bg-[#103037] text-white"
                          : "bg-white border border-[#10303733] text-[#10303799] hover:bg-black/5"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                  <div className="hidden flex-shrink-0 md:block w-[1rem]" />
                </div>

                {/* Right gradient overlay */}
                <div className="hidden absolute top-0 right-0 z-10 h-full bg-gradient-to-l from-white to-transparent pointer-events-none md:block w-[6rem]" />

                {/* Right scroll button */}
                <button
                  onClick={() => scroll("right")}
                  className="hidden absolute top-1/2 z-20 justify-center items-center text-white rounded-full transition-all -translate-y-1/2 pointer-events-auto md:flex right-[1rem] w-[4rem] h-[4rem] bg-[#103037] hover:bg-[#103037]/90"
                  aria-label="Scroll right"
                >
                  <svg
                    className="w-[1.2rem] h-[1.2rem]"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 8 11"
                    fill="none"
                  >
                    <path
                      d="M0.668945 0.743164L5.66895 5.24316L0.668945 9.74316"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              {/* Search bar */}
              <div className="relative w-[min(56.125rem,100%)] flex-shrink-0">
                <svg
                  className="absolute top-1/2 z-10 -translate-y-1/2 pointer-events-none left-[3rem] w-[2rem] h-[2rem] text-[#10303799]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search posts..."
                  className="w-full leading-none border-none focus:ring-2 focus:outline-none bg-[#D9D9D961] rounded-[1.5rem] pl-[6rem] pr-[3rem] py-[1.85rem] text-[1.75rem] text-[#103037] placeholder:text-[#10303799] lg:text-[2rem] focus:ring-[#103037]/20"
                />
              </div>
            </div>
          </div>
        )}

        <div className="w-[min(180rem,100%)] flex flex-col items-center">
          <div className="grid grid-cols-1 w-full md:grid-cols-2 lg:grid-cols-3 gap-x-[3rem] gap-y-[4rem]">
            {displayedItems.map((post, index) => (
              <AnimatePresence key={post._id + "index" + index}>
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    },
                  }}
                  viewport={{ once: true }}
                >
                  <Link
                    key={post._id}
                    href={post.slug}
                    className="block h-full border overflow-clip group rounded-[1.5rem] border-black/10"
                  >
                    <div className="relative h-full bg-white p-[2.25rem]">
                      {post.banner && (
                        <div className="relative overflow-clip aspect-[16/9] rounded-[1.5rem]">
                          <div className="absolute z-10 w-full h-full pointer-events-none p-[2rem]">
                            {post.tags && post.tags.length > 0 && (
                              <div className="flex flex-wrap pointer-events-auto gap-[1rem] mb-[2rem]">
                                {post.tags.map((tag, tagIndex) => (
                                  <div
                                    key={tagIndex}
                                    className="rounded-full bg-[#85D0A6] py-[0.75rem] px-[1.25rem] w-fit"
                                  >
                                    <p className="m-0 text-[#103037] font-[400] text-[1.75rem] leading-[140%]">
                                      {tag}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <Image
                            src={post.banner}
                            alt={post.title}
                            className="object-cover !h-full group-hover:scale-105 transition-all duration-500 rounded-[1.5rem]"
                            quality={90}
                          />
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="mt-[3rem] mb-[1rem] text-[#103037]/50 leading-[100%] text-[2rem] flex h-fit [&_time]:h-fit">
                          {post.pubDate && (
                            <FormattedDate
                              dateTimeString={post.pubDate}
                              format="LLLL d, yy"
                            />
                          )}
                        </div>
                        <h3 className="text-[3rem] lg:text-[3rem] font-[500] text-black leading-[120%] line-clamp-2 mb-[1.5rem]">
                          {post.title}
                        </h3>
                        {post.content && (
                          <p className="text-black/60 text-[1.75rem] lg:text-[2rem] leading-[150%] line-clamp-3 mb-[1.5rem] m-0">
                            {extractTextFromPortableText(post.content, 200)}
                          </p>
                        )}

                        <div className="flex items-center text-black gap-[1rem] text-[2rem]">
                          Read more
                          <svg
                            className="w-[1rem]"
                            xmlns="http://www.w3.org/2000/svg"
                            width="8"
                            height="11"
                            viewBox="0 0 8 11"
                            fill="none"
                          >
                            <path
                              d="M0.668945 0.743164L5.66895 5.24316L0.668945 9.74316"
                              stroke="#6DA991"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              </AnimatePresence>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-[4rem] lg:mt-[6rem]"
            >
              <button
                className="flex relative justify-center items-center w-full group button cta button-green group gap-[1.25rem] overflow-clip"
                data-type="default"
                onClick={handleLoadMore}
              >
                <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg"></div>
                <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover"></div>
                <div className="relative leading-none overflow-clip">
                  <div className="transition-transform duration-500 group-hover:-translate-y-full button-text">
                    Load More
                  </div>
                  <div className="absolute top-0 left-0 transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-text-hover">
                    Load More
                  </div>
                </div>
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
